import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PaymentMethod } from "../models/payment";
import { indefiniteArticle, matchesMethod } from "../utils/methodNumberCheck";

/**
 * The number-against-method check, run when the payer presses Continue.
 *
 * `check()` returns whether the payer may go on and, if not, raises the
 * message. Numbers cannot move between operators, so a mismatch is refused,
 * not warned about. The message clears as soon as the method, the number or
 * the country changes, and is built at render so a language switch follows.
 */
const useMethodNumberCheck = (
  method: PaymentMethod | null | undefined,
  national: string,
  countryCode: string
) => {
  const { t } = useTranslation();
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    setRefused(false);
  }, [method?.id, national, countryCode]);

  const name = (method?.name || method?.name_short || "").trim();
  const message =
    refused && name
      ? indefiniteArticle(name) === "an"
        ? t("payment.phone.errors.method_mismatch_an", { name })
        : t("payment.phone.errors.method_mismatch_a", { name })
      : "";

  // A method with no name cannot be named in the message, and a refusal the
  // payer cannot read is worse than no check, so it is not refused.
  const check = (): boolean => {
    const ok = !name || matchesMethod(method, national, countryCode);
    setRefused(!ok);
    return ok;
  };

  return { message, check };
};

export default useMethodNumberCheck;
